import type { Dimension } from '@/graphql/brokerage-settings';
import type { BrokerageMember, BrokerageRole, MemberRole } from '@/graphql/brokerages';

/** The member roles a brokerage offers, in the order the picker shows them. */
export const MEMBER_ROLES: readonly { role: MemberRole; name: string; summary: string }[] = [
  {
    role: 'OWNER',
    name: 'Account owner',
    summary: 'Everything, including billing and removing admins.',
  },
  { role: 'ADMIN', name: 'Admin', summary: 'Everything except what only the owner can do.' },
  {
    role: 'ADVISOR',
    name: 'Advisor',
    summary: 'Works their own cases and the brokerage’s shared tools.',
  },
  { role: 'AUDIT', name: 'Audit', summary: 'Can look at everything and change nothing.' },
];

const OTHER_ROLE_NAMES: Partial<Record<MemberRole, string>> = {
  PENDING: 'Pending',
  SYSTEM: 'System',
  STAFF: 'LendWell staff',
};

export function memberRoleName(role: string | null | undefined): string {
  const upper = (role ?? '').toUpperCase() as MemberRole;
  return (
    MEMBER_ROLES.find((entry) => entry.role === upper)?.name ?? OTHER_ROLE_NAMES[upper] ?? 'No role'
  );
}

/** Keystone holds no names, so a member reads as the end of their main-app id. */
export function memberLabel(member: BrokerageMember, meId: string | null | undefined): string {
  if (member.personId && member.personId === meId) {
    return 'You';
  }
  const ref =
    member.person?.extAppApiId ?? member.person?.extTurnstileId ?? member.personId ?? member.id;
  return `Member ${ref.slice(-6)}`;
}

/** The roles beyond the main one that a member holds: the ones composed in this brokerage. */
export function extraRoles(
  member: BrokerageMember,
  roles: readonly BrokerageRole[],
): BrokerageRole[] {
  return roles.filter(
    (role) =>
      role.presetKey === null &&
      member.personId !== null &&
      role.assigneeIds.includes(member.personId),
  );
}

/** Live members first, each group in the order they joined. */
export function sortMembers(members: readonly BrokerageMember[]): BrokerageMember[] {
  return [...members].sort((a, b) => {
    if (Boolean(a.deletedAt) !== Boolean(b.deletedAt)) {
      return a.deletedAt ? 1 : -1;
    }
    return (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
  });
}

/** Move one id a step up or down, returning the whole new order, or null at an end. */
export function stepOrder(ids: readonly string[], id: string, direction: -1 | 1): string[] | null {
  const index = ids.indexOf(id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= ids.length) {
    return null;
  }
  const next = [...ids];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** A brokerage's display name: its own name, else the name its emails carry. */
export function brokerageName(brokerage: {
  name: string | null;
  companyName: string | null;
}): string {
  return brokerage.name || brokerage.companyName || 'Unnamed brokerage';
}

/** What reaching a status means, or null while it is still in progress. */
export function outcomeName(item: Dimension): string | null {
  if (item.isTerminalSuccess) {
    return 'Ends successfully';
  }
  if (item.isTerminalFailure) {
    return 'Ends unsuccessfully';
  }
  return null;
}
